import { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import GroupIcon from '@material-ui/icons/Group';
import AddCommentIcon from '@material-ui/icons/AddComment';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import { Button } from '@material-ui/core';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Link } from '@backstage/core-components';
import type { Entity } from '@backstage/catalog-model';

const useStyles = makeStyles(theme => ({
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[6],
    },
  },
  actionArea: {
    flexGrow: 0,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: theme.spacing(3),
  },
  chipsContainer: {
    padding: theme.spacing(0, 3, 2, 3),
  },
  proposeContainer: {
    padding: theme.spacing(0, 3, 3, 3),
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  icon: {
    fontSize: 48,
    marginBottom: theme.spacing(1.5),
    color: theme.palette.primary.main,
  },
  title: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  description: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    marginBottom: theme.spacing(2),
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    justifyContent: 'center',
  },
  chipLink: {
    textDecoration: 'none',
  },
}));

export const CommunitiesOfPracticeCard = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);

  const [entities, setEntities] = useState<Entity[] | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    catalogApi
      .getEntities({
        filter: { kind: 'Component', 'metadata.tags': 'cop' },
        fields: [
          'metadata.name',
          'metadata.namespace',
          'metadata.title',
          'metadata.description',
        ],
      })
      .then(result => {
        if (!cancelled) {
          setEntities(result.items);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [catalogApi]);

  return (
    <Card className={classes.card}>
      <CardActionArea
        component={Link}
        to="/catalog?filters%5Bkind%5D=component&filters%5Btags%5D=cop"
        className={classes.actionArea}
      >
        <CardContent className={classes.content}>
          <GroupIcon className={classes.icon} />
          <Typography variant="h6" className={classes.title}>
            Communities of Practice
          </Typography>
          <Typography className={classes.description}>
            Cross-team knowledge sharing
          </Typography>
        </CardContent>
      </CardActionArea>
      <Box className={classes.chipsContainer}>
        <div className={classes.chips}>
          {loading && <Chip label="Loading…" size="small" />}
          {!loading && entities && entities.length === 0 && (
            <Chip label="No CoPs yet" size="small" disabled />
          )}
          {entities?.map(entity => {
            const ns = entity.metadata.namespace ?? 'default';
            const name = entity.metadata.name;
            const label = entity.metadata.title ?? name;
            return (
              <Link
                key={name}
                to={`/docs/${ns}/component/${name}`}
                className={classes.chipLink}
              >
                <Chip
                  label={label}
                  size="small"
                  color="primary"
                  variant="outlined"
                  clickable
                />
              </Link>
            );
          })}
        </div>
      </Box>
      <Box className={classes.proposeContainer}>
        <Link
          to="/docs/default/component/saltsquare-cops"
          className={classes.chipLink}
        >
          <Button size="small" color="primary" startIcon={<MenuBookIcon />}>
            Guide
          </Button>
        </Link>
        <Link
          to="/create/templates/default/cop-propose-agenda-topic"
          className={classes.chipLink}
        >
          <Button size="small" color="primary" startIcon={<AddCommentIcon />}>
            Propose topic
          </Button>
        </Link>
      </Box>
    </Card>
  );
};
